import React, {FC, useEffect, useReducer, useState, useRef} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import ProductGeneralForm from './general/ProductGeneralForm'
import {fieldReducer, IProductProps, productActionType, productInitialState} from '../cors/reducers'
import '../../../stylesheet/ProductForm.css'
import {useIntl} from "react-intl";
import {addProduct} from "../cors/_requests";
import SharedData from "../../common/SharedData";
import {useNavigate, useParams} from "react-router-dom";
import AlertMessage from "../../common/alert/AlertMsg";
import SpecialPriceDecisionModal from './components/SpecialPriceDecisionModal';
import NewSpecialPriceModal from './components/NewSpecialPriceModal';
import {tenantTaxEnabled} from '../../../tenant/tenantState';
import {isShopUser} from '../../common/stockScope';

class MissingFieldsError extends Error {
    constructor(public details: string[]) {
        super('missing_fields');
        this.name = 'MissingFieldsError';
    }
}

class InvalidFieldValuesError extends Error {
    constructor(public details: string[]) {
        super('invalid_values');
        this.name = 'InvalidFieldValuesError';
    }
}

class VariationValidationError extends Error {
    constructor(public details: string[]) {
        super('variation_validation');
        this.name = 'VariationValidationError';
    }
}

class FormValidationError extends Error {
    constructor(public details: string[]) {
        super('form_validation');
        this.name = 'FormValidationError';
    }
}

interface ValidationResult {
    missingFields: string[];
    invalidValues: string[];
    variationIssues: string[];
}

const ProductGeneralTab: FC<IProductProps & { saveSignal?: number; onSubmittingChange?: (v: boolean) => void }> = ({ prodState, prodDispatch, saveSignal, onSubmittingChange }) => {

    const { formatMessage } = useIntl();
    const [state, dispatch] = useReducer(fieldReducer, productInitialState)
    const {currentUser, language} = SharedData()
    const {productID} = useParams()
    const [currentStep, setCurrentStep] = useState( productID ? 2 : 0);
    const navigate = useNavigate();
    const [key, setKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [errorMsg, setErrorMsg] = useState({
        title: '',
        message: '',
    })
    const [originalPrice, setOriginalPrice] = useState<string>('')
    const [showSpecialPriceModal, setShowSpecialPriceModal] = useState(false)
    const [showNewSpecialPriceModal, setShowNewSpecialPriceModal] = useState(false)
    const [calculatedNewSpecialPrice, setCalculatedNewSpecialPrice] = useState<string>('0')
    const [specialPriceDecision, setSpecialPriceDecision] = useState<'deactivate' | 'keep_active' | null>(null)
    const originalPriceRef = useRef<string>('')
    const steps = [
        'barcode',
        'Product Category',
        'English Product Info',
    ];
    const handleCloseAlert = () => {
        setErrorMsg({ title: '', message: '' });
        dispatch({ type: productActionType.hasError, payload: { value: false } });
    };

    useEffect(() => {
        if (productID && prodState.salePrice.value) {
            const currentPrice = String(prodState.salePrice.value).trim()
            if (!originalPriceRef.current) {
                originalPriceRef.current = currentPrice
                setOriginalPrice(currentPrice)
            }
        }
    }, [productID]);

    useEffect(() => {
        if (productID && prodState.salePrice.value && !originalPriceRef.current) {
            const currentPrice = String(prodState.salePrice.value).trim()
            originalPriceRef.current = currentPrice
            setOriginalPrice(currentPrice)
        }
    }, [prodState.salePrice.value]);

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const isContinueDisabled = () => {
        if (currentStep === 0) {
            return !prodState.barcode.value || prodState.barcode.error;
        }
        if (currentStep === 1) {
            return !prodState.categoryID.value;
        }
        return false;
    };
    const isSubmitDisabled = () => {
        return !(prodState.manufacturer_id.value && (!tenantTaxEnabled() || prodState.tax_class_id.value) && prodState.recommendedAge.value &&
            prodState.model.value && prodState.productNameEn.value && prodState.productNameAr.value &&
            (((prodState.variations.length !== 0 && !prodState.quantity.value && !prodState.salePrice.value) ||
                (prodState.quantity.value && prodState.salePrice.value)
            )
            ))
    };

    const validateForm = (): ValidationResult => {
        dispatch({ type: productActionType.clearErrors, payload: { id: '', value: '' } });
        const result: ValidationResult = { missingFields: [], invalidValues: [], variationIssues: [] };
        // About Product (EN+AR) and Description (EN+AR) are now REQUIRED;
        // Recommended age is now OPTIONAL. Only these skip the required check.
        let notValidateKeys = ['expiryDate', 'recommendedAge'];

        if (prodState.variations.length > 0) {
            notValidateKeys.push('salePrice', 'quantity', 'image');
        }

        // Map raw field keys to translated labels so validation errors are
        // localized (e.g. "recommendedAge" -> "العمر الموصى به") instead of
        // showing the raw English key to the user.
        const fieldLabels: Record<string, string> = {
            barcode: 'barcode', model: 'model_number', categoryID: 'category',
            productNameEn: 'prod_name_en', productNameAr: 'prod_name_ar',
            manufacturer_id: 'brand', recommendedAge: 'recommended_age',
            quantity: 'quantity', salePrice: 'sale_price', tax_class_id: 'tax_class',
            image: 'image',
            aboutEn: 'about_prod_en', aboutAr: 'about_prod_ar',
            productDescriptionEn: 'desc_en', productDescriptionAr: 'desc_ar',
        };
        const labelOf = (k: string) => (fieldLabels[k] ? formatMessage({ id: fieldLabels[k] }) : k);

        Object.keys(prodState).forEach((item) => {
            const key = prodState[item as keyof typeof prodState];

            if (key && typeof key === 'object') {
                if (key.hasOwnProperty('error')) {
                    if (!notValidateKeys.includes(item) &&
                        (key.value === '' || typeof key.value === 'undefined') ||
                        (item === 'image' && typeof key.value === 'string' && !notValidateKeys.includes('image') && key.value.includes('logos/img-placeholder.png'))) {
                        result.missingFields.push(labelOf(item));
                        dispatch({ type: productActionType.fieldRequired, payload: { id: item, value: true } });
                    }
                    if (prodState.variations.length === 0 && ((item === 'quantity' && key.value < 0) || (item === 'salePrice' && key.value <= 0))) {
                        result.invalidValues.push(formatMessage({ id: 'field_must_be_positive' }, { field: labelOf(item) }));
                        dispatch({ type: productActionType.fieldRequired, payload: { id: item, value: true } });
                    }
                }
            }

            if (item === 'variations') {
                let variationError = false;

                prodState.variations.forEach((variation, index) => {
                    const indexS = index + 1;
                    if (!variation.barcode) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'barcode' })}`);
                        variationError = true;
                    }
                    if (!variation.color_id) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'color' })}`);
                        variationError = true;
                    }
                    if (!variation.size_id) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'size' })}`);
                        variationError = true;
                    }
                    if (!variation.quantity || variation.quantity <= '0' || variation.quantity === '') {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'quantity' })}`);
                        variationError = true;
                    }
                    if (!variation.price || variation.price <= '0' || variation.price === '') {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'price' })}`);
                        variationError = true;
                    }
                });
                if (prodState.variationImages.length !== prodState.variations.length) {
                    for (let i = prodState.variationImages.length; i < prodState.variations.length - prodState.variationImages.length; i++) {
                        result.variationIssues.push(`${formatMessage({ id: 'variation_missing_img' })} ${i + 1}`);
                    }
                    variationError = true;
                }

                if (variationError) {
                    setKey('variations');
                }
            }
        });

        return result;
    };
    const collectMessages = (response: any, depth: number = 0): string[] => {
        const messages: string[] = [];

        if (typeof response === 'object' && response !== null) {
            for (const key in response) {
                if (response.hasOwnProperty(key)) {
                    if (Array.isArray(response[key])) {
                        messages.push(`${''.repeat(depth * 2)}${key}:`);
                        response[key].forEach((item: any, index: number) => {
                            messages.push(`${''.repeat((depth + 1) * 2)}${index}:`);
                            messages.push(...collectMessages(item, depth + 2));
                        });
                    }
                    else if (typeof response[key] === 'object') {
                        messages.push(`${''.repeat(depth * 2)}${key}:`);
                        messages.push(...collectMessages(response[key], depth + 1));
                    }
                    else {
                        messages.push(`${''.repeat(depth * 2)}${key}: ${response[key]}`);
                    }
                }
            }
        } else {
            messages.push(response);
        }

        return messages;
    };

        const showAlert = (title: string, message: string) => {
            setErrorMsg({
                title,
                message,
            })
            dispatch({type: productActionType.hasError, payload: {value: true}})
            setIsSubmitting(false);
        }

    const performSave = async (specialPriceAction?: 'deactivate' | 'keep_active') => {
        let loadingStarted = false;
        try {
            const validation = validateForm();
            const populated = [
                validation.missingFields.length > 0,
                validation.invalidValues.length > 0,
                validation.variationIssues.length > 0,
            ].filter(Boolean).length;

            if (populated > 1) {
                throw new FormValidationError([
                    ...validation.missingFields,
                    ...validation.invalidValues,
                    ...validation.variationIssues,
                ]);
            }
            if (validation.missingFields.length > 0) {
                throw new MissingFieldsError(validation.missingFields);
            }
            if (validation.invalidValues.length > 0) {
                throw new InvalidFieldValuesError(validation.invalidValues);
            }
            if (validation.variationIssues.length > 0) {
                throw new VariationValidationError(validation.variationIssues);
            }

            dispatch({type: productActionType.isLoading})
            loadingStarted = true;
            setIsSubmitting(true);
            const resp:any = await addProduct(language, currentUser?.token, prodState, productID, specialPriceAction, isShopUser(currentUser));
            const messages = collectMessages(resp);
            const seenMessages = new Set<string>();
            const pushMessage = (msg: unknown) => {
                if (typeof msg === 'string' && msg && !seenMessages.has(msg)) {
                    seenMessages.add(msg);
                }
            };

            if (resp && resp.errors) {
                Object.values(resp.errors).forEach((group: any) => {
                    if (group && typeof group === 'object') {
                        Object.values(group).forEach((message: any) => {
                            if (typeof message === 'string') {
                                pushMessage(message);
                            } else if (message && typeof message === 'object') {
                                pushMessage(Object.values(message)[0]);
                            }
                        });
                    } else if (typeof group === 'string') {
                        pushMessage(group);
                    }
                });
            }
            const formatedErrorMessageFinal = Array.from(seenMessages);

            if (!resp || resp.errors) {
                if (resp && resp.errors) {
                    const payload = {errors:resp.errors}
                    dispatch({type: productActionType.validationErrors,payload});
                    showAlert(formatMessage({id: 'something_wrong_title_add_prod'}),
                        formatedErrorMessageFinal.length > 0
                            ? formatedErrorMessageFinal.join('\n')
                            : messages[messages.length - 1])
                    return false;
                } else {
                    throw new Error('')
                }
            } else {
                // Clear the image after successful submission
                prodDispatch({type: productActionType.image, payload: {id: 'image', value: toAbsoluteUrl('/media/logos/img-placeholder.png')}});
                localStorage.setItem('thankYouPageURL', window.location.href);
                navigate(`/thank-you?go=request-${productID ? 'update' : 'add'}-products&message=${encodeURIComponent(resp.data.message)}`);
            }
        } catch (e) {
            if (e instanceof MissingFieldsError) {
                showAlert(
                    formatMessage({ id: 'missing_required_fields_title' }),
                    `${formatMessage({ id: 'missing_required_fields_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof InvalidFieldValuesError) {
                showAlert(
                    formatMessage({ id: 'invalid_field_values_title' }),
                    `${formatMessage({ id: 'invalid_field_values_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof VariationValidationError) {
                showAlert(
                    formatMessage({ id: 'variation_validation_title' }),
                    `${formatMessage({ id: 'variation_validation_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof FormValidationError) {
                showAlert(
                    formatMessage({ id: 'form_validation_failed_title' }),
                    `${formatMessage({ id: 'form_validation_failed_message' })}: ${e.details.join(', ')}`
                );
            } else {
                showAlert(
                    formatMessage({ id: 'something_wrong_title_add_prod' }),
                    e instanceof Error && e.message
                        ? e.message
                        : formatMessage({ id: 'something_wrong_desc_add_prod' })
                );
            }
        } finally {
            if (loadingStarted) {
                dispatch({type: productActionType.isLoading})
            }
            setIsSubmitting(false)
        }
    }

    const saveProduct = async () => {
        if (productID && originalPriceRef.current) {
            const currentPrice = String(prodState.salePrice.value || '').trim()
            const originalPrice = String(originalPriceRef.current || '').trim()
            
            const currentPriceNum = parseFloat(currentPrice) || 0
            const originalPriceNum = parseFloat(originalPrice) || 0
            const priceChanged = Math.abs(currentPriceNum - originalPriceNum) > 0.01
            
            if (priceChanged) {
                const currentSpecialPrice = prodState.current_special_price

                if (currentSpecialPrice != null) {
                    const currentPriceNum = parseFloat(currentPrice) || 0
                    const specialPriceStr = String(currentSpecialPrice)

                    if (currentPriceNum < currentSpecialPrice) {
                        showAlert(
                            formatMessage({ id: 'price_below_special_title' }),
                            formatMessage(
                                { id: 'price_below_special_message' },
                                {
                                    newPrice: currentPrice,
                                    specialPrice: specialPriceStr,
                                }
                            )
                        )
                        return
                    }

                    setShowSpecialPriceModal(true)
                    return
                }
            }
        }

        await performSave()
    }

    const handleDeactivateSpecialPrice = async () => {
        setSpecialPriceDecision('deactivate')
        setShowSpecialPriceModal(false)
        await performSave('deactivate')
        setSpecialPriceDecision(null)
    }

    const handleKeepSpecialPriceActive = () => {
        setSpecialPriceDecision('keep_active')
        setShowSpecialPriceModal(false)

        const specialPriceNum = Number(prodState.current_special_price ?? 0) || 0
        const originalPriceNum = parseFloat(originalPriceRef.current || '0') || 0
        const currentPriceNum = parseFloat(prodState.salePrice.value || '0') || 0
        
        if (originalPriceNum > 0 && specialPriceNum > 0) {
            const discountPercentage = ((originalPriceNum - specialPriceNum) / originalPriceNum) * 100
            const newSpecialPriceNum = currentPriceNum * (1 - discountPercentage / 100)
            const newSpecialPrice = newSpecialPriceNum.toFixed(4)
            setCalculatedNewSpecialPrice(newSpecialPrice)
            setShowNewSpecialPriceModal(true)
        } else {
            performSave('keep_active')
        }
    }

    const handleConfirmNewSpecialPrice = async () => {
        setShowNewSpecialPriceModal(false)
        await performSave('keep_active')
        setSpecialPriceDecision(null)
    }

    const handleCancelNewSpecialPrice = () => {
        setShowNewSpecialPriceModal(false)
        setSpecialPriceDecision(null)
        setIsSubmitting(false)
    }

    const handleCloseSpecialPriceModal = () => {
        setShowSpecialPriceModal(false)
        setIsSubmitting(false)
    }

    // Once a draft is opened here it is submit-only: the page's single action is
    // "Save Changes" (saveProduct), which runs validateForm — including the
    // negative-quantity check. There is deliberately no save-as-draft path.

    // Surface the submitting state so the header "Save Changes" button (in
    // EditProduct) can disable itself while a save is in flight.
    useEffect(() => {
        onSubmittingChange?.(isSubmitting);
    }, [isSubmitting]);

    // The header "Save Changes" button lives in the parent; it bumps saveSignal
    // to trigger the same saveProduct flow (validation + alerts) used before.
    useEffect(() => {
        if (saveSignal && saveSignal > 0) {
            saveProduct();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveSignal]);

    return (
        <>
            <ProductGeneralForm
                prodState={prodState}
                prodDispatch={prodDispatch}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                key2={key}
                isSubmitDisabled={isSubmitDisabled()}
            />
            {errorMsg.title && errorMsg.message && (
                <AlertMessage
                    title={errorMsg.title}
                    message={errorMsg.message}
                    onClose={handleCloseAlert}
                />
            )}

                {showSpecialPriceModal && (
                <SpecialPriceDecisionModal
                    show={showSpecialPriceModal}
                    onClose={handleCloseSpecialPriceModal}
                    onDeactivate={handleDeactivateSpecialPrice}
                    onKeepActive={handleKeepSpecialPriceActive}
                    oldPrice={originalPriceRef.current || prodState.salePrice.value}
                    newPrice={prodState.salePrice.value}
                    specialPrice={String(prodState.current_special_price ?? '')}
                />
            )}

            {showNewSpecialPriceModal && (
                <NewSpecialPriceModal
                    show={showNewSpecialPriceModal}
                    onConfirm={handleConfirmNewSpecialPrice}
                    onCancel={handleCancelNewSpecialPrice}
                    newSpecialPrice={calculatedNewSpecialPrice}
                />
            )}

        </>
    );
};

export default ProductGeneralTab
